import { useEffect } from 'react';
import { absoluteUrl, APP_NAME, localizedPath, PUBLIC_ROUTES, SITE_URL } from '../config/seo';
import { dictionaries, localeOptions, useLanguage, type Locale } from '../i18n';

function upsertMeta(selector: string, attributes: Record<string, string>): void {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.append(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value));
}

function upsertLink(selector: string, attributes: Record<string, string>): void {
  let element = document.head.querySelector<HTMLLinkElement>(selector);
  if (!element) {
    element = document.createElement('link');
    document.head.append(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value));
}

function routePath(path: string): string {
  const clean = path === '/' ? '/' : path.replace(/\/$/, '');
  return PUBLIC_ROUTES.includes(clean as (typeof PUBLIC_ROUTES)[number]) ? clean : '/';
}

function jsonLd(locale: Locale, path: string): unknown[] {
  const title = dictionaries[locale]['meta.title'];
  const description = dictionaries[locale]['meta.description'];
  const canonical = absoluteUrl(localizedPath(routePath(path), locale));
  const faqItems = [1, 2, 3, 4].map((index) => ({
    '@type': 'Question',
    name: dictionaries[locale][`landing.faq${index}Q`],
    acceptedAnswer: {
      '@type': 'Answer',
      text: dictionaries[locale][`landing.faq${index}A`]
    }
  }));

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: APP_NAME,
      applicationCategory: 'MultimediaApplication',
      operatingSystem: 'Any',
      url: canonical,
      description,
      inLanguage: localeOptions.map((option) => option.htmlLang),
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      featureList: [
        'Add photo borders',
        'Apply shadows and rounded corners',
        'Display EXIF metadata',
        'Export JPG and PNG locally',
        'Batch export images'
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      name: `${title} FAQ`,
      mainEntity: faqItems
    }
  ];
}

export function Seo({ path }: { path: string }): null {
  const { locale, t } = useLanguage();

  useEffect(() => {
    const publicPath = routePath(path);
    const canonical = absoluteUrl(localizedPath(publicPath, locale));
    const title = t('meta.title');
    const description = t('meta.description');
    const isPublic = PUBLIC_ROUTES.includes((path === '/' ? '/' : path.replace(/\/$/, '')) as (typeof PUBLIC_ROUTES)[number]);

    document.title = title;
    document.documentElement.lang = localeOptions.find((option) => option.value === locale)?.htmlLang ?? 'en';

    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[name="keywords"]', { name: 'keywords', content: t('meta.keywords') });
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: isPublic ? 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1' : 'noindex,follow'
    });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: APP_NAME });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: absoluteUrl('/og.svg') });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: absoluteUrl('/og.svg') });

    upsertLink('link[rel="canonical"]', { rel: 'canonical', href: canonical });
    localeOptions.forEach((option) => {
      upsertLink(`link[rel="alternate"][hreflang="${option.htmlLang}"]`, {
        rel: 'alternate',
        hreflang: option.htmlLang,
        href: absoluteUrl(localizedPath(publicPath, option.value))
      });
    });
    upsertLink('link[rel="alternate"][hreflang="x-default"]', {
      rel: 'alternate',
      hreflang: 'x-default',
      href: absoluteUrl(localizedPath(publicPath, 'en'))
    });

    let script = document.head.querySelector<HTMLScriptElement>('script[data-lumaframe-jsonld]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.dataset.lumaframeJsonld = 'true';
      document.head.append(script);
    }
    script.textContent = JSON.stringify(jsonLd(locale, publicPath));
  }, [locale, path, t]);

  return null;
}
